import { type Company, StartSetupPaymentParams, UpdateBillingParams, UpdateCompanyParams } from "@pentacode/core";
import { css, html } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { app } from "../init";
import { shared } from "../styles";
import { loadStripe, type StripeClient, type StripeIbanElement } from "../vendor/stripe";
import { alert } from "./alert-dialog";
import { Dialog } from "./dialog";

const NO_BILLING_KEY_MESSAGE =
    "Zahlungsmittel können momentan leider nicht eingerichtet werden. " +
    "Besteht das Problem weiterhin, bitte den Support kontaktieren.";

@customElement("ptc-billing-dialog")
export class BillingDialog extends Dialog<Company, boolean> {
    @property()
    private _validationError = "";

    @query("form")
    private _form: HTMLFormElement;

    @query("input[name='email']")
    private _emailInput: HTMLInputElement;

    @query("input[name='name']")
    private _nameInput: HTMLInputElement;

    private _company: Company;

    private _stripe: StripeClient | null = null;

    private _ibanElement: StripeIbanElement;
    private _cardElement?: HTMLDivElement;

    async show(company: Company) {
        this._company = company;
        const promise = super.show();

        await this.updateComplete;

        this._emailInput.value = company.email || (company.owner && company.owner.email) || "";
        this._nameInput.value = company.name;

        this._stripe = await loadStripe();
        if (!this._stripe) {
            this.done(false);
            void alert(NO_BILLING_KEY_MESSAGE, { type: "warning" });
            return promise;
        }
        await this._createIbanElement(this._stripe, company.country);

        return promise;
    }

    private async _createIbanElement(stripe: StripeClient, country: "DE" | "AT") {
        if (this._cardElement) {
            this.removeChild(this._cardElement);
        }

        const elements = stripe.elements({});

        const options = {
            supportedCountries: ["SEPA"],
            // Elements can use a placeholder as an example IBAN that reflects
            // the IBAN format of your customer's country. If you know your
            // customer's country, we recommend passing it to the Element as the
            // placeholderCountry.
            placeholderCountry: country,
        };

        // Create an instance of the IBAN Element
        this._ibanElement = elements.create("iban", options);

        this._cardElement = document.createElement("div");
        this.appendChild(this._cardElement);
        this._ibanElement.mount(this._cardElement);

        this._ibanElement.addEventListener("change", (e) => {
            this._validationError = e.error?.message || "";
        });

        return new Promise((resolve) => this._ibanElement.addEventListener("ready", resolve));
    }

    private async _submit(e: FocusEvent) {
        e.preventDefault();

        if (!this._stripe) {
            return;
        }

        this.loading = true;

        try {
            const { secret: clientSecret } = await app.api.startSetupPayment(new StartSetupPaymentParams());

            const data = new FormData(this._form);

            const result = await this._stripe.confirmSepaDebitSetup(clientSecret, {
                payment_method: {
                    sepa_debit: this._ibanElement,
                    billing_details: {
                        name: data.get("name"),
                        email: data.get("email"),
                    },
                },
            });

            if (result.error) {
                throw result.error;
            }

            await app.api.updateCompany(
                new UpdateCompanyParams({
                    id: this._company.id,
                    billing: new UpdateBillingParams({
                        addPaymentMethod: result.setupIntent.payment_method,
                    }),
                })
            );

            await app.fetchCompany();
        } catch (e) {
            this.hide();
            await alert(e.message, { type: "warning" });
            this._show();
        }

        this.loading = false;

        this.done(true);
    }

    static styles = [
        shared,
        ...Dialog.styles,
        css`
            h3 {
                text-align: center;
                margin: 0;
            }

            .inner {
                padding: 1em;
            }

            .stripe-element {
                border: solid 1px var(--shade-2);
                padding: 0.7em;
                border-radius: 4px;
            }

            .actions > :not(:last-child) {
                margin-right: 0.7em;
            }
        `,
    ];

    renderContent() {
        return html`
            <form @submit=${this._submit}>
                <h3>Zahlungsmittel Einrichten</h3>

                <div class="horizontal layout fields">
                    <div class="field stretch">
                        <label>Kontoinhaber</label>
                        <input name="name" placeholder="Kontoinhaber" required />
                    </div>

                    <div class="field stretch">
                        <label>Emailadresse</label>
                        <input name="email" type="email" placeholder="Emailadresse" required />
                    </div>
                </div>

                <div class="field">
                    <label>IBAN</label>

                    <div class="stripe-element">
                        <slot></slot>
                    </div>

                    <div id="error message" ?hidden=${!this._validationError}>${this._validationError}</div>
                </div>

                <div class="horizontal layout actions">
                    <button class="primary stretch" id="submit-button">Abschicken</button>

                    <button class="stretch" type="button" @click=${() => this.done(false)}>Abbrechen</button>
                </div>
            </form>
        `;
    }
}
